import { useId, useState, type FormEvent } from "react";
import { useCreateProviderCredential } from "../../api/queries";
import type { ProviderKind } from "../../api/types";
import { Button } from "../../components/Button";
import { Modal } from "../../components/Modal";
import { useToast } from "../../toast/ToastContext";
import "../../components/Form.css";

interface AddProviderCredentialModalProps {
  open: boolean;
  onClose: () => void;
}

interface CredentialFields {
  hetznerToken: string;
}

const EMPTY_FIELDS: CredentialFields = {
  hetznerToken: "",
};

// `kind` is threaded through even though only "hetzner" exists today so the
// per-provider branch is ready when DigitalOcean/Vultr/Linode are added.
function tokenForSubmit(_kind: ProviderKind, fields: CredentialFields): string {
  return fields.hetznerToken;
}

function canSubmitCredentials(_kind: ProviderKind, fields: CredentialFields): boolean {
  return fields.hetznerToken.length > 0;
}

export function AddProviderCredentialModal({ open, onClose }: AddProviderCredentialModalProps) {
  const labelId = useId();
  const kindId = useId();
  const tokenId = useId();
  const { pushToast } = useToast();
  const mutation = useCreateProviderCredential();
  const [label, setLabel] = useState("");
  const [kind, setKind] = useState<ProviderKind>("hetzner");
  const [fields, setFields] = useState<CredentialFields>(EMPTY_FIELDS);

  function handleClose() {
    setLabel("");
    setKind("hetzner");
    setFields(EMPTY_FIELDS);
    mutation.reset();
    onClose();
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const submittedLabel = label.trim();
    const token = tokenForSubmit(kind, fields);
    mutation.mutate(
      { kind, label: submittedLabel, token },
      {
        onSuccess: (created) => {
          pushToast({ title: `Credential "${created.label}" added.`, tone: "success" });
          handleClose();
        },
        onError: (err) => {
          pushToast({
            title: `Couldn't add "${submittedLabel}"`,
            description: err instanceof Error ? err.message : "Try again in a moment.",
            tone: "error",
          });
        },
      },
    );
    // Handed off to the mutation above — clear secrets from state immediately (S-13).
    setFields(EMPTY_FIELDS);
  }

  return (
    <Modal open={open} title="Add a provider credential" onClose={handleClose} initialFocusId={labelId}>
      <form onSubmit={handleSubmit} noValidate>
        <p className="form-note">
          Comebye uses this credential only to provision and manage compute on your behalf — it's sealed on write
          and never shown again.
        </p>

        <label className="form-field" htmlFor={kindId}>
          <span>Provider</span>
          <select
            id={kindId}
            value={kind}
            onChange={(e) => {
              setKind(e.target.value as ProviderKind);
              setFields(EMPTY_FIELDS);
            }}
          >
            <option value="hetzner">Hetzner</option>
          </select>
        </label>

        <label className="form-field" htmlFor={labelId}>
          <span>Label</span>
          <input
            id={labelId}
            type="text"
            autoComplete="off"
            spellCheck={false}
            placeholder="Hetzner — prod project"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            required
            maxLength={100}
          />
        </label>

        <label className="form-field" htmlFor={tokenId}>
          <span>Hetzner API token</span>
          <input
            id={tokenId}
            type="password"
            autoComplete="off"
            value={fields.hetznerToken}
            onChange={(e) => setFields({ ...fields, hetznerToken: e.target.value })}
            required
          />
          <span className="form-field__hint">
            Sealed on write. Never shown again — add a new credential later if you need to replace it.
          </span>
        </label>

        <div className="form-actions">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={mutation.isPending || !label.trim() || !canSubmitCredentials(kind, fields)}
          >
            {mutation.isPending ? "Adding…" : "Add credential"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
